import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Calculator } from 'lucide-react';
import { LT } from '../../contexts/AppCatalogueContext';
import { COPY, tr } from './copy';
import { cellFor, NEXT_RE, rowIcon, rowIndex, stageFor, toneFor } from './model';
import { CellText, MiniLadder, Tagline } from './shared';

/* Levels · one level at a time. The compact staircase stays on screen,
 * ← / → move between levels, the topics are tabs instead of one long
 * wall, and the next step (what it takes + the thresholds) sits beside. */
export default function LevelExplorer({ map, canon, locale, keywordRow, level, onLevel, onSimulate }) {
  const n = level;
  const max = map.levels[map.levels.length - 1]?.n || 9;
  const l = map.levels.find(x => x.n === n) || map.levels[0];
  const stage = stageFor(map, n);
  const tone = toneFor(map, n);
  const nextRow = rowIndex(canon, NEXT_RE);

  // Topics = every row except the keywords (the headline) and "to move up"
  // (the side card). Icons come from the French labels.
  const topics = useMemo(() => map.rows
    .map((row, ri) => ({ row, ri, Icon: rowIcon(canon.rows[ri]?.label) }))
    .filter(t => t.ri !== keywordRow && t.ri !== nextRow && cellFor(t.row, n)),
  [map, canon, keywordRow, nextRow, n]);
  const [topic, setTopic] = useState('all');
  useEffect(() => { if (topic !== 'all' && !topics.some(t => t.ri === topic)) setTopic('all'); }, [topics, topic]);

  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      if (e.key === 'ArrowRight' && n < max) onLevel(n + 1);
      if (e.key === 'ArrowLeft' && n > 1) onLevel(n - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [n, max, onLevel]);

  const keyword = keywordRow >= 0 ? cellFor(map.rows[keywordRow], n) : null;
  const nextCell = nextRow >= 0 ? cellFor(map.rows[nextRow], n) : null;
  const step = map.promotion?.steps?.[String(n)];
  const shown = topic === 'all' ? topics : topics.filter(t => t.ri === topic);

  return (
    <div className="mt-4">
      <MiniLadder map={map} value={n} onPick={onLevel} levelWord={tr(COPY.level, locale)} />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 min-w-0">
          {/* Level hero */}
          <header className="flex items-start gap-5">
            <span className="h-[72px] w-[72px] shrink-0 grid place-items-center font-display text-[2.4rem] leading-none"
                  style={{ background: tone.fill, color: tone.ink }}>{n}</span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: LT.green50 }}>
                {stage?.key} · {stage?.title}
              </div>
              <h2 className="font-display text-[2rem] sm:text-[2.4rem] leading-[1.05] mt-1" style={{ color: LT.greenDeep }}>{l?.name}</h2>
              <div className="font-num text-[13px] mt-1" style={{ color: LT.green50 }}>{l?.years}</div>
              {keyword && <Tagline text={keyword.text} />}
            </div>
            <div className="hidden sm:flex gap-1.5 shrink-0">
              <button type="button" onClick={() => onLevel(n - 1)} disabled={n <= 1} aria-label="previous"
                      className="h-10 w-10 rounded-full grid place-items-center disabled:opacity-30" style={{ border: '1px solid rgba(43,65,56,0.2)', color: LT.greenDeep }}>
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => onLevel(n + 1)} disabled={n >= max} aria-label="next"
                      className="h-10 w-10 rounded-full grid place-items-center disabled:opacity-30" style={{ border: '1px solid rgba(43,65,56,0.2)', color: LT.greenDeep }}>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </header>

          {/* Topic tabs */}
          <div className="mt-6 flex flex-wrap gap-1.5" role="tablist">
            {[{ ri: 'all', label: tr(COPY.allTopics, locale) }, ...topics.map(t => ({ ri: t.ri, label: t.row.label, Icon: t.Icon }))].map(t => {
              const on = topic === t.ri;
              const Icon = t.Icon;
              return (
                <button key={t.ri} type="button" role="tab" aria-selected={on} onClick={() => setTopic(t.ri)}
                        className="inline-flex items-center gap-1.5 text-[13.5px] px-3.5 py-1.5 rounded-full transition"
                        style={on ? { background: LT.greenDeep, color: LT.beigeLight } : { border: '1px solid rgba(43,65,56,0.2)', color: LT.greenDeep }}>
                  {Icon && <Icon className="h-3.5 w-3.5" />} {t.label}
                </button>
              );
            })}
          </div>

          <div className="mt-5 space-y-5">
            {shown.map(({ row, ri, Icon }) => {
              const cell = cellFor(row, n);
              return (
                <section key={ri} className="p-5 sm:p-6" style={{ background: LT.beigeLight, border: '1px solid rgba(43,65,56,0.12)' }}>
                  <h3 className="flex items-center gap-2 text-[15px]" style={{ color: LT.greenDeep }}>
                    <span className="h-8 w-8 grid place-items-center rounded-full" style={{ background: tone.soft }}><Icon className="h-4 w-4" /></span>
                    {row.label}
                    {cell.levels.length > 1 && (
                      <span className="ml-auto text-[11.5px] font-num" style={{ color: LT.green50 }}>
                        {tr(COPY.shared, locale)} {cell.levels[0]}–{cell.levels[cell.levels.length - 1]}
                      </span>
                    )}
                  </h3>
                  <div className="mt-3 text-[14.5px] leading-relaxed" style={{ color: '#3a4f44' }}><CellText text={cell.text} /></div>
                </section>
              );
            })}
          </div>
          <p className="hidden sm:block mt-4 text-[12px]" style={{ color: LT.green50 }}>{tr(COPY.keysHint, locale)}</p>
        </div>

        {/* Next step */}
        <aside className="lg:col-span-4 min-w-0">
          <div className="lg:sticky lg:top-[180px] p-5 sm:p-6 space-y-4" style={{ background: LT.greenDeep, color: LT.beigeLight }}>
            <div className="text-[11px] uppercase tracking-[0.14em] opacity-70">{tr(COPY.nextStep, locale)}</div>
            {n >= max ? (
              <p className="text-[15px]">{tr(COPY.terminal, locale)}</p>
            ) : (
              <>
                <button type="button" onClick={() => onLevel(n + 1)} className="flex items-center gap-3 text-left group">
                  <span className="h-10 w-10 grid place-items-center font-num text-[17px]" style={{ background: toneFor(map, n + 1).fill, color: toneFor(map, n + 1).ink }}>{n + 1}</span>
                  <span>
                    <span className="block text-[12px] opacity-70">{tr(COPY.toLevel, locale)} {n + 1}</span>
                    <span className="block text-[17px] group-hover:underline underline-offset-4">{map.levels.find(x => x.n === n + 1)?.name}</span>
                  </span>
                </button>
                {step && (
                  <div className="flex flex-wrap gap-1.5 text-[12px]">
                    <span className="px-2.5 py-1 rounded-full" style={{ background: LT.yellow, color: LT.greenDeep }}>
                      {tr(COPY.avgNeed, locale)} {String(step.average).replace('.', locale === 'en' ? '.' : ',')}
                    </span>
                    <span className="px-2.5 py-1 rounded-full" style={{ border: '1px solid rgba(251,251,250,0.35)' }}>
                      {tr(COPY.evalCount, locale, { n: step.evaluations })}
                    </span>
                    <span className="px-2.5 py-1 rounded-full" style={{ border: '1px solid rgba(251,251,250,0.35)' }}>
                      {tr(COPY.approval[step.approval], locale)}
                    </span>
                    {step.stage_change && (
                      <span className="px-2.5 py-1 rounded-full" style={{ border: `1px solid ${LT.yellow}`, color: LT.yellow }}>{tr(COPY.stageChange, locale)}</span>
                    )}
                  </div>
                )}
                {nextCell && (
                  <div className="text-[13.5px] leading-relaxed" style={{ color: '#dfe6e1' }}>
                    <div className="text-[11px] uppercase tracking-[0.12em] opacity-70 mb-1.5">{tr(COPY.expected, locale)}</div>
                    <CellText text={nextCell.text} compact />
                  </div>
                )}
                <button type="button" onClick={() => onSimulate(n)}
                        className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full text-[14.5px] transition hover:opacity-90"
                        style={{ background: LT.cta, color: '#fff' }}>
                  <Calculator className="h-4 w-4" /> {tr(COPY.simulate, locale)} <ArrowRight className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
